import { TripStatus } from "@/api.types.v2";

/**
 * Dispatch-v2 status color taxonomy — single source of truth.
 *
 * Status pills across the board (Lookup, waypoint detail, the rail trip pills)
 * read their bg/fg/label from here, so a color/label change propagates
 * everywhere. Lane colors live in `task-type-meta` (LANE_META); this file owns
 * the shipment- and trip-status palettes + the care accent.
 */
export interface StatusStyle {
  bg: string;
  fg: string;
  label: string;
}

/** Shipment lifecycle status → pill style. */
export const SHIPMENT_STATUS_META: Record<string, StatusStyle> = {
  PENDING_PAYMENT: { bg: "#f4f4f5", fg: "#52525b", label: "Pending payment" },
  LABEL_READY: { bg: "#e0e7ff", fg: "#4338ca", label: "Label ready" },
  PICKED_UP: { bg: "#ede9fe", fg: "#6d28d9", label: "Picked up" },
  IN_TRANSIT: { bg: "#ede9fe", fg: "#6d28d9", label: "In transit" },
  AT_FACILITY: { bg: "#e0e7ff", fg: "#4338ca", label: "At facility" },
  OUT_FOR_DELIVERY: { bg: "#ffedd5", fg: "#c2410c", label: "Out for delivery" },
  DELIVERED: { bg: "#dcfce7", fg: "#15803d", label: "Delivered" },
  AVAILABLE_FOR_PICKUP: {
    bg: "#e0e7ff",
    fg: "#4338ca",
    label: "Ready for pickup",
  },
  DELIVERY_ATTEMPTED: { bg: "#fef3c7", fg: "#b45309", label: "Attempted" },
  EXCEPTION: { bg: "#fee2e2", fg: "#b91c1c", label: "Exception" },
  ON_HOLD: { bg: "#fef3c7", fg: "#b45309", label: "On hold" },
  RETURNED: { bg: "#fef3c7", fg: "#b45309", label: "Returned" },
  FAILED: { bg: "#fee2e2", fg: "#b91c1c", label: "Failed" },
  CANCELLED: { bg: "#fee2e2", fg: "#b91c1c", label: "Cancelled" },
  // Synthetic display status (not a ShipmentStatus enum member): shown while
  // a cancellation request awaits admin review — see shipmentDisplayStatus().
  CANCELLATION_PENDING: {
    bg: "#fef3c7",
    fg: "#b45309",
    label: "Cancellation pending",
  },
};

/**
 * Status key a shipment should DISPLAY as. While a cancellation request is
 * under admin review the cascade leaves the shipment in EXCEPTION (operators
 * can only FAIL a task, never cancel) — but "Exception" for a routine
 * customer cancel reads as something broke. Present "Cancellation pending"
 * instead; the stored status is untouched.
 */
export function shipmentDisplayStatus(s: {
  status: string;
  cancellationRequestedAt?: string | null;
}): string {
  return s.cancellationRequestedAt ? "CANCELLATION_PENDING" : s.status;
}

/** Fallback for an unknown status (label derived by the caller). */
export const NEUTRAL_STATUS: StatusStyle = {
  bg: "#f4f4f5",
  fg: "#52525b",
  label: "",
};

/** Resolve a shipment status to its style, with a neutral fallback whose label
 * is the de-underscored status string. */
export function shipmentStatusStyle(status: string): StatusStyle {
  return (
    SHIPMENT_STATUS_META[status] ?? {
      ...NEUTRAL_STATUS,
      label: status.replace(/_/g, " "),
    }
  );
}

/** Trip status → pill style. */
export const TRIP_STATUS_META: Record<string, StatusStyle> = {
  [TripStatus.ACTIVE]: { bg: "#e0e7ff", fg: "#4338ca", label: "Active" },
  [TripStatus.PLANNED]: { bg: "#fef3c7", fg: "#b45309", label: "Planned" },
  [TripStatus.COMPLETED]: { bg: "#dcfce7", fg: "#15803d", label: "Completed" },
  [TripStatus.ARCHIVED]: { bg: "#f4f4f5", fg: "#52525b", label: "Archived" },
};

/** Care signal accent (add-on services / fragile / cold chips). */
export const CARE_TEAL = "#0d9488";
